/**
 * /checkout/cancel — Stripe Checkout return (cancelled). PRD-003 PR-03, T-028.
 *
 * Stripe redirects here when the subscriber backs out of Checkout. Nothing was
 * charged and no state changed. The page offers two doors back: re-enter the
 * subscription flow on the marketing site, or hit the contact form (R-01: the
 * contact path is expected to carry meaningful conversion load at this price
 * point, so a clean handoff matters here).
 *
 * Both CTAs link out to the marketing site (yesplease.studio) after the
 * 2026-05-28 architecture pivot moved discovery / subscribe off the app.
 *
 * Voice: Eric's client-communication register — no guilt, an open hand.
 */

import { createFileRoute } from '@tanstack/react-router';
import type { Tier } from '@studio/api-schemas';

interface CheckoutCancelSearch {
  tier: Tier;
}

function validateSearch(
  search: Record<string, unknown>,
): CheckoutCancelSearch {
  const raw =
    typeof search.tier === 'string' ? search.tier.toLowerCase() : '';
  const tier: Tier = raw === 'pro' ? 'pro' : 'studio';
  return { tier };
}

export const Route = createFileRoute('/checkout/cancel')({
  validateSearch,
  component: CheckoutCancelPage,
});

function CheckoutCancelPage() {
  const { tier } = Route.useSearch();

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-16">
      <div className="w-full max-w-lg space-y-6">
        <h1 className="font-display text-3xl text-foreground">
          No charge. No problem.
        </h1>
        <div className="space-y-4 text-base leading-relaxed text-foreground">
          <p>
            You stepped out of checkout before finishing. Nothing was charged
            and nothing changed.
          </p>
          <p>
            If the timing or the format is the question rather than the price,
            tell me what you are weighing. I would rather hear it than have you
            guess.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <a
            href={`https://yesplease.studio/services/studio#pricing`}
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
            data-tier={tier}
          >
            Back to the {tier === 'pro' ? 'Pro' : 'Studio'} plan
          </a>
          <a
            href="https://yesplease.studio/contact"
            className="inline-flex items-center justify-center rounded-md border border-input px-5 py-2.5 text-sm font-medium text-foreground"
          >
            I have a question first
          </a>
        </div>
      </div>
    </main>
  );
}
